import React from "react";
import { Flame, ArrowRight } from "lucide-react";
import { EVENT_GROUP_COLOR, eventDescriptor, formatEvent, isHighPriorityEvent } from "@/utils/eventLabels";

const fmtRel = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return "adesso";
  if (diffMin < 60) return `${diffMin}m fa`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h fa`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}g fa`;
};

/**
 * "🔥 Avvisi recenti" — prominent feed of high-priority events on the admin dashboard.
 * Renders the last N hot events (acquisti, hot leads, video started, 80% visione, contact request)
 * with one-click navigation to the user detail page.
 */
export default function HotEventsPanel({ events, limit = 12 }) {
  const hot = (events || [])
    .filter((e) => isHighPriorityEvent(e.event_name))
    .slice(0, limit);

  return (
    <section data-testid="hot-events-panel"
      className="bg-white rounded-2xl border overflow-hidden mb-7"
      style={{ borderColor: "var(--np-secondary)", boxShadow: "0 20px 60px -25px rgba(217,94,57,0.35)" }}>
      <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-[#fbe5da]"
        style={{ background: "linear-gradient(135deg, #fff4ef 0%, #ffffff 80%)" }}>
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--np-secondary)" }}>
            <Flame size={17} color="#fff" />
          </span>
          <div>
            <p className="text-base font-bold leading-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
              Avvisi recenti
            </p>
            <p className="text-[11px]" style={{ color: "var(--np-muted)" }}>
              Eventi ad alto valore commerciale — chiama o scrivi subito
            </p>
          </div>
        </div>
        <a href="/admin/eventi" data-testid="hot-events-see-all"
          className="text-xs font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: "var(--np-secondary)" }}>
          Vedi tutti <ArrowRight size={12} />
        </a>
      </div>

      {hot.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <p className="text-sm" style={{ color: "var(--np-muted)" }}>
            Nessun avviso recente. Quando un lead inizia una lezione, raggiunge l&apos;80% di visione, richiede info o acquista, comparirà qui in tempo reale.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-[#f1f5f9]">
          {hot.map((e, i) => {
            const d = formatEvent(e.event_name, e.payload || {});
            const color = EVENT_GROUP_COLOR[eventDescriptor(e.event_name).group] || "var(--np-secondary)";
            const p = e.payload || {};
            const userId = p.user_id || p.user?.id;
            const name = p.name || p.user?.name || "—";
            const email = p.email || p.user?.email || "";
            const phone = p.phone || p.user?.phone || "";
            const amount = p.amount_cents ? `€ ${(p.amount_cents / 100).toFixed(2)}` : null;
            return (
              <li key={`${e.created_at}-${i}`}
                data-testid={`hot-event-row-${i}`}
                onClick={() => userId && (window.location.href = `/admin/utente/${userId}`)}
                className={`px-5 py-3.5 flex items-start gap-3 ${userId ? "cursor-pointer hover:bg-[#fafcfe]" : ""}`}>
                <span className="mt-0.5 inline-flex w-8 h-8 items-center justify-center rounded-full text-base shrink-0"
                  style={{ background: "#fff4ef", color }}>
                  {d.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-tight" style={{ color: "var(--np-primary)" }}>
                    {d.label}
                  </p>
                  <div className="text-[12px] mt-0.5 flex flex-wrap items-center gap-x-2" style={{ color: "var(--np-muted)" }}>
                    <span className="font-medium" style={{ color: "var(--np-primary)" }}>{name}</span>
                    {email && <span className="truncate max-w-[200px]">{email}</span>}
                    {phone && <span>· {phone}</span>}
                    {amount && <span className="font-bold" style={{ color: "var(--np-secondary)" }}>· {amount}</span>}
                  </div>
                </div>
                <span className="text-[11px] whitespace-nowrap shrink-0" style={{ color: "var(--np-muted)" }}>{fmtRel(e.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
